import { from, shareReplay, switchMap } from "rxjs"
import { finalize } from "rxjs/operators"

import { myPrincipalQueryKey } from "@/hooks/useProfile.ts"
import { type ApiClient, batchInfo } from "@/lib/apiClient/client.ts"
import { normalizeProjectKey } from "@/lib/std.ts"
import { taskDetailsKey, taskKey } from "@/routes/tasks/tasksHooks.ts"

import type { AppDateTime, InitializedChannel, Pair } from "@/lib/client/api-types.ts"
import type {
  AiAgentTask,
  AiAgentTaskDetails,
  AiAgentTaskEvent,
  AiChatEvent,
  AiChatMessage,
  AvailableLog,
  Batch,
  BatchInfo,
  CPrincipal,
  DevTaskStats,
  ExecCommandResult,
  ExecCommandResultUpdated,
  FigmaStatus,
  FileContentPart,
  GitCommitChange,
  GitFile,
  GitHubPrInfo,
  GitHubStatus,
  GrazieLLMProfileDTO,
  GrazieLLMRequest,
  InlineDiff,
  LLMLogEntry,
  LLMLogPage,
  LlmQuota,
  MessagesSorting,
  OrganizationMember,
  OrganizationPermissions,
  PR_Project2,
  ProfileIdentifier,
  ProjectCodingGuidelines,
  ProjectKey,
  ProjectPermissions,
  ProjectSecret,
  ProjectSettings,
  RemoteUrlValidationResult,
  RepositoryAuthType,
  RepositoryFetchStatus,
  RuntimeConfig,
  SshKeyData,
  TD_MemberProfile,
  TID,
  TypingEventDTO,
  UPreviewProcessesApiState,
  UProfileIdentifier,
  WarmupInfo,
  WarmupSnapshot
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { QueryClient } from "@tanstack/react-query"
import type { Observable } from "rxjs"


type MutatingApiClient = Pick<ApiClient,
  "addPreviewCommand" |
  "addProjectSecret" |
  "addSshKey" |
  "cancelTaskProcess" |
  "checkoutTaskCheckpoint" |
  "completePreviewRuntimeSetup" |
  "configurePreviewRuntime" |
  "continuePreviewRuntimeConfigWithSecrets" |
  "continuePreviewRuntimeConfigWithServices" |
  "continueTaskProcess" |
  "createInvitation" |
  "createProjectWarmupSnapshot" |
  "createSampleProject" |
  "createTask" |
  "deleteProject" |
  "deleteProjectSecret" |
  "deleteProjectWarmupSnapshot" |
  "deleteSecretInSetupTask" |
  "deleteSshKey" |
  "deleteTask" |
  "disconnectFigmaAccount" |
  "editFiles" |
  "editMessage" |
  "installRemoteMCP" |
  "inviteMatterSupport" |
  "markAsSuccessful" |
  "openPrInGitHub" |
  "putUserKeysIntoPreviewEnv" |
  "readMessage" |
  "recreatePreview" |
  "removeOrganizationMember" |
  "removePreviewCommand" |
  "removeProjectMember" |
  "reportCreatePullRequestClickedEvent" |
  "reportTaskDraftGeneratedEvent" |
  "resetProjectCreationFlow" |
  "resetSlackToken" |
  "restartPreview" |
  "retryTaskProcess" |
  "saveBeforeCommand" |
  "savePreviewCommand" |
  "savePreviewPort" |
  "sendMessage" |
  "sendTypingEvent" |
  "setOrganizationMemberRole" |
  "setProfilePreference" |
  "setProjectMemberRole" |
  "setProjectName" |
  "setRepositoryAccess" |
  "setRepositoryAuth" |
  "setRepositoryUrl" |
  "showPersonalTokenHelp" |
  "startFetch" |
  "startFigmaOAuthFlow" |
  "startGitHubOAuthFlow" |
  "startProjectCreation" |
  "startSlackUserForNotificationsOAuthFlow" |
  "stopBeforeCommand" |
  "stopPreviewCommand" |
  "subscribeToPreviewAppLogs" |
  "toggleFavoriteProject" |
  "updateProjectCodingGuidelines" |
  "updateProjectColor" |
  "updateProjectCover" |
  "updateProjectName" |
  "updateProjectRootFolder" |
  "updateProjectSecret" |
  "updateProjectSettings" |
  "updateRepositoryAuth" |
  "updateSecretInSetupTask" |
  "updateTaskGuidelines" |
  "updateTaskSettings" |
  "updateTaskTitle" |
  "uploadFile" |
  "verifyBeforeCommand" |
  "verifyPreviewCommand"
>

// STOP!
// Mutating methods should go to MutatingApiClient, fetching methods should be made available via Queries to enable caching.
// Adding methods here should be an exception, not a rule.
type DirectAccessApiClient = Pick<ApiClient,
  "downloadAttachment" |
  "downloadDomElementScreenshot" |
  "downloadFile" |
  "downloadPatch" |
  "downloadPreviewEnvLogs" |
  "findFiles" | // useFindFiles hook
  "getBranches" | // useBranches hook
  "getOrganizationPermissions" | // useOrganizationPermissions hook
  "getProjectPermissions" | // useProjectPermissions hook
  "listFiles" | // useListFiles hook
  "profile" // useProfile hook
>

// The Queries interface hides most of the ApiClient interface from the rest of the codebase,
// exposing only a part of it via LimitedApiClient
export type LimitedApiClient = MutatingApiClient & DirectAccessApiClient & {
  buildStaticUrl(path: string): string
  getHost(): string
}

export interface Queries {
  readonly apiClient: LimitedApiClient

  archivedMessages(taskId: string, batchInfo: BatchInfo, blockMessageIds: string[], sorting: MessagesSorting): Promise<Batch<AiChatMessage>>

  canSshToPreviewEnv(previewId: string | null, previewV2EnvId: string | null): Observable<boolean>

  chatHistory(taskId: string, messageId: string): Promise<AiChatMessage[]>

  commandOutputUpdates(taskId: string): Observable<ExecCommandResultUpdated>

  favoriteProjects(): Promise<ProjectKey[]>

  findFiles(projectKey: ProjectKey, repo: string, commit: string | null, pattern: string, batchInfo: BatchInfo): Promise<Batch<GitFile>>

  getCommitChanges(batchInfo: BatchInfo, projectKey: ProjectKey, repoName: string, commits: string[]): Promise<GitCommitChange[]>

  getExecCommandResult(execResultId: TID, skipStdOut: number, skipStdErr: number): Promise<ExecCommandResult>

  getFetchStatus(projectKey: ProjectKey): Promise<RepositoryFetchStatus>

  getFileContentBytes(projectKey: ProjectKey, repo: string, blobId: string, skip: number, limit: number): Promise<FileContentPart>

  getGitHubConfigurationUrl(projectKey: ProjectKey, repoName: string): Promise<string>

  getGitHubStatus(projectKey: ProjectKey, repoName: string): Promise<InitializedChannel<GitHubStatus, GitHubStatus>>

  getInlineDiff(projectKey: ProjectKey, repoName: string, file: GitCommitChange): Promise<InlineDiff | null>

  getLlmProfileIds(): Promise<GrazieLLMProfileDTO[]>

  getOrgQuota(): Promise<LlmQuota>

  getOrganizationMembers(batchInfo: BatchInfo): Promise<Batch<OrganizationMember>>

  getOrganizationPermissions(): Promise<OrganizationPermissions>

  getPooledPreview(projectKey: ProjectKey, repositoryName: string): Promise<TID | null>

  getPrShortSummary(taskId: string): Promise<string | null>

  getProfilePreference(key: string): Promise<string | null>

  getProjectCodingGuidelines(projectKey: ProjectKey, repository: string): Promise<ProjectCodingGuidelines | null>

  getProjectMembers(batchInfo: BatchInfo, projectKey: ProjectKey): Promise<Batch<Pair<TD_MemberProfile, boolean>>>

  getProjectPermissions(projectKey: ProjectKey): Promise<ProjectPermissions>

  getProjectRuntimeConfig(projectKey: ProjectKey, repository: string): Promise<Pair<RuntimeConfig, boolean> | null>

  getProjectSettings(projectKey: ProjectKey): Promise<ProjectSettings>

  getProjectV2(projectKey: ProjectKey): Promise<PR_Project2>

  getRepositoryAuthOptions(url: string): Promise<RepositoryAuthType[]>

  getRuntimeEnvironmentDescription(taskId: TID): Promise<string>

  getSlackNotificationsEnabled(): Promise<boolean>

  getSshConnectCommandForPreview(previewId: string | null, previewV2EnvId: string | null): Promise<string>

  getSshTunnelCommandForPreview(previewId: string | null, previewV2Id: string | null): Promise<string>

  getTaskGuidelines(taskId: string): Promise<ProjectCodingGuidelines | null>

  getTaskStats(taskId: string): Promise<InitializedChannel<DevTaskStats, DevTaskStats>>

  getWarmupInfo(projectKey: ProjectKey, repository: string): Promise<WarmupInfo>

  listPreviewSetupTasks(projectKey: ProjectKey, repository: string, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  listProfiles(batchInfo: BatchInfo, query: string): Promise<TD_MemberProfile[]>

  listProjectSecrets(projectKey: ProjectKey): Promise<ProjectSecret[]>

  listProjectWarmupSnapshots(projectKey: ProjectKey, repoName: string): Promise<WarmupSnapshot[]>

  listProjectsV2(batchInfo: BatchInfo): Promise<Batch<PR_Project2>>

  listSshKeys(profile: UProfileIdentifier): Promise<SshKeyData[]>

  listTasks(projectKey: ProjectKey, repo: string, principalId: string | null, successful: boolean, withCollaborators: boolean | null, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  listTasksForUser(userId: TID, successful: boolean, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  liveUpdateDebugEvents(): Observable<AppDateTime>

  llmLog(taskId: string, pageId: string | null, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<LLMLogEntry>>

  llmLogPageUpdates(taskId: string, pageId: string | null): Observable<LLMLogPage>

  llmLogPages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<LLMLogPage>>

  llmLogRequestContext(logEntryId: string): Promise<GrazieLLMRequest>

  llmLogUpdates(taskId: string, pageId: string | null): Observable<LLMLogEntry>

  me(): Promise<CPrincipal>

  message(messageId: string): Promise<AiChatMessage>

  messageUpdates(taskId: string): Observable<AiChatEvent>

  messages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<AiChatMessage>>

  profile(profile: ProfileIdentifier.Username): Promise<TD_MemberProfile>

  resolvePrincipals(pids: string[]): Promise<Pair<string, CPrincipal>[]>

  subscribeToFigmaAuthStatus(): Promise<InitializedChannel<FigmaStatus, FigmaStatus>>

  subscribeToOpenedPullRequests(taskIds: string[]): Promise<InitializedChannel<Pair<TID, GitHubPrInfo[]>[], Pair<TID, GitHubPrInfo[]>[]>>

  subscribeToPreviewAppLogs(id: string | null, previewEnvId: string | null, logId: string): Observable<string>

  subscribeToPreviewLogList(id: string | null, previewV2Id: string | null): Observable<AvailableLog[]>

  subscribeToPreviewProcess(previewId: string | null, previewV2Id: string | null): Observable<UPreviewProcessesApiState>

  subscribeToPreviewReadyEstimate(id: TID): Observable<number>

  subscribeToRepositorySyncErrors(projectKey: ProjectKey, repository: string): Promise<InitializedChannel<RepositoryFetchStatus, RepositoryFetchStatus>>

  subscribeToTypingEvents(taskId: string): Observable<TypingEventDTO>

  task(taskId: string): Promise<AiAgentTask>

  taskDetails(taskId: string): Promise<AiAgentTaskDetails>

  taskUpdates(taskId: string): Observable<AiAgentTaskEvent>

  validateRemoteUrl(url: string): Promise<RemoteUrlValidationResult>
}

export class QueriesImpl implements Queries {
  constructor(readonly apiClient: ApiClient, private readonly queryClient: QueryClient) {
  }

  private trackedCanSshToPreviewEnv = new Map<TID, Observable<boolean>>()

  archivedMessages(taskId: string, batchInfo: BatchInfo, blockMessageIds: string[], sorting: MessagesSorting) {
    return this.queryClient.fetchQuery({
      queryKey: ["archivedMessages", taskId, batchInfo.offset, batchInfo.batchSize, ...blockMessageIds, sorting],
      queryFn: () => this.apiClient.archivedMessages(taskId, batchInfo, blockMessageIds, sorting),
    })
  }

  canSshToPreviewEnv(previewId: string | null, previewV2EnvId: string | null) {
    return getOrCreateSharedObservable(this.trackedCanSshToPreviewEnv, `${previewId}/${previewV2EnvId}`, () => this.apiClient.canSshToPreviewEnv(previewId as TID | null, previewV2EnvId as TID | null))
  }

  chatHistory(taskId: string, messageId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["chatHistory", messageId, taskId],
      queryFn: async () => {
        const before = await this.apiClient.messages(taskId, batchInfo(1000, messageId), "FromNewestToOldest")

        const current = await this.apiClient.message(messageId)
        return [...before.data, current]
      }
    })
  }

  private trackedCommandOutputUpdates = new Map<TID, Observable<ExecCommandResultUpdated>>()

  commandOutputUpdates(taskId: TID) {
    return getOrCreateSharedObservable(this.trackedCommandOutputUpdates, taskId, () => this.apiClient.commandOutputUpdates(taskId))
  }

  favoriteProjects() {
    return this.queryClient.fetchQuery({
      queryKey: favoriteProjectsQueryKey,
      queryFn: () => this.apiClient.favoriteProjects(),

      // Make sure to invalidate when toggling a favorite project
      staleTime: Infinity,
    })
  }

  findFiles(projectKey: ProjectKey, repo: string, commit: string | null, pattern: string, batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: ["findFiles", normalizeProjectKey(projectKey), repo, commit, pattern, batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.findFiles(projectKey, repo, commit, pattern, batchInfo),
    })
  }

  getCommitChanges(batchInfo: BatchInfo, projectKey: ProjectKey, repoName: string, commits: string[]) {
    return this.queryClient.fetchQuery({
      queryKey: ["getCommitChanges", normalizeProjectKey(projectKey), repoName, ...commits, batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.getCommitChanges(batchInfo, projectKey, repoName, commits),
    })
  }

  getExecCommandResult(execResultId: TID, skipStdOut: number, skipStdErr: number) {
    return this.queryClient.fetchQuery({
      queryKey: ["getExecCommandResult", execResultId, skipStdOut, skipStdErr],
      queryFn: () => this.apiClient.getExecCommandResult(execResultId, skipStdOut, skipStdErr),
    })
  }

  getFetchStatus(projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      queryKey: ["getFetchStatus", normalizeProjectKey(projectKey)],
      queryFn: () => this.apiClient.getFetchStatus(projectKey),
    })
  }

  getFileContentBytes(projectKey: ProjectKey, repo: string, blobId: string, skip: number, limit: number) {
    return this.queryClient.fetchQuery({
      queryKey: ["getFileContent", normalizeProjectKey(projectKey), repo, blobId, skip, limit],
      queryFn: () => this.apiClient.getFileContentBytes(projectKey, repo, blobId, skip, limit)
    })
  }

  getGitHubConfigurationUrl(projectKey: ProjectKey, repoName: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getGitHubConfigurationUrl", normalizeProjectKey(projectKey), repoName],
      queryFn: () => this.apiClient.getGitHubConfigurationUrl(projectKey, repoName),
    })
  }

  getGitHubStatus(projectKey: ProjectKey, repoName: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getGitHubStatus"],
      queryFn: () => this.apiClient.getGitHubStatus(projectKey, repoName),
    })
  }

  getInlineDiff(projectKey: ProjectKey, repoName: string, file: GitCommitChange) {
    return this.queryClient.fetchQuery({
      queryKey: ["getInlineDiff", normalizeProjectKey(projectKey), repoName, file],
      queryFn: () => this.apiClient.getInlineDiff(projectKey, repoName, file),
    })
  }

  getLlmProfileIds() {
    return this.queryClient.fetchQuery({
      queryKey: ["getLlmProfileIds"],
      queryFn: () => this.apiClient.getLlmProfileIds(),

      // This entity is managed externally, so this query might become stale. For now, we accept this tradeoff.
      staleTime: Infinity,
    })
  }

  getOrgQuota() {
    return this.queryClient.fetchQuery({
      queryKey: ["getOrgQuota"],
      queryFn: () => this.apiClient.getOrgQuota()
    })
  }

  getOrganizationMembers(batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: ["getOrganizationMembers", batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.getOrganizationMembers(batchInfo),
    })
  }

  getOrganizationPermissions() {
    return this.queryClient.fetchQuery({
      queryKey: ["getOrganizationPermissions"],
      queryFn: () => this.apiClient.getOrganizationPermissions(),

      staleTime: Infinity,
    })
  }

  getPooledPreview(projectKey: ProjectKey, repositoryName: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getPooledPreview", normalizeProjectKey(projectKey), repositoryName],
      queryFn: () => this.apiClient.getPooledPreview(projectKey, repositoryName),
    })
  }

  getPrShortSummary(taskId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getPrShortSummary", taskId],
      queryFn: () => this.apiClient.getPrShortSummary(taskId),
    })
  }

  getProfilePreference(key: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getProfilePreference", key],
      queryFn: () => this.apiClient.getProfilePreference(key),

      // Make sure to invalidate the profile preference when it changes
      staleTime: Infinity,
    })
  }

  getProjectCodingGuidelines(projectKey: ProjectKey, repository: string) {
    return this.queryClient.fetchQuery({
      queryKey: projectCodingGuidelinesQueryKey(projectKey, repository),
      queryFn: () => this.apiClient.getProjectCodingGuidelines(projectKey, repository),
    })
  }

  getProjectMembers(batchInfo: BatchInfo, projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      queryKey: ["getProjectMembers", normalizeProjectKey(projectKey), batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.getProjectMembers(batchInfo, projectKey),
    })
  }

  getProjectPermissions(projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      queryKey: ["getProjectPermissions", normalizeProjectKey(projectKey)],
      queryFn: () => this.apiClient.getProjectPermissions(projectKey),

      // For now, the permissions can't be edited
      staleTime: Infinity,
    })
  }

  getProjectRuntimeConfig(projectKey: ProjectKey, repository: string) {
    return this.queryClient.fetchQuery({
      queryKey: projectRuntimeConfigQueryKey(projectKey, repository),
      queryFn: () => this.apiClient.getProjectRuntimeConfig(projectKey, repository),
    })
  }

  getProjectSettings(projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      queryKey: ["getProjectSettings", normalizeProjectKey(projectKey)],
      queryFn: () => this.apiClient.getProjectSettings(projectKey),
    })
  }

  getProjectV2(projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      ...projectQueryKey(projectKey),
      queryFn: () => this.apiClient.getProjectV2(projectKey),

      // Make sure to invalidate the project when its properties (the cover image, for example) are changed
      staleTime: Infinity,
    })
  }

  getRepositoryAuthOptions(url: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getRepositoryAuthOptions", url],
      queryFn: () => this.apiClient.getRepositoryAuthOptions(url),
    })
  }

  getRuntimeEnvironmentDescription(taskId: TID) {
    return this.queryClient.fetchQuery({
      queryKey: ["getRuntimeEnvironmentDescription", taskId],
      queryFn: () => this.apiClient.getRuntimeEnvironmentDescription(taskId),
    })
  }

  getSlackNotificationsEnabled() {
    return this.queryClient.fetchQuery({
      queryKey: ["getSlackNotificationsEnabled"],
      queryFn: () => this.apiClient.getSlackNotificationsEnabled(),
    })
  }

  getSshConnectCommandForPreview(previewId: string | null, previewV2EnvId: string | null) {
    return this.queryClient.fetchQuery({
      queryKey: ["getSshConnectCommandForPreview", previewId, previewV2EnvId],
      queryFn: () => this.apiClient.getSshConnectCommandForPreview(previewId as TID | null, previewV2EnvId as TID | null),
    })
  }

  getSshTunnelCommandForPreview(previewId: string | null, previewV2Id: string | null) {
    return this.queryClient.fetchQuery({
      queryKey: ["getSshTunnelCommandForPreview", previewId, previewV2Id],
      queryFn: () => this.apiClient.getSshTunnelCommandForPreview(previewId as TID | null, previewV2Id as TID | null),
    })
  }

  getTaskGuidelines(taskId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getTaskGuidelines", taskId],
      queryFn: () => this.apiClient.getTaskGuidelines(taskId),
    })
  }

  getTaskStats(taskId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getTaskStats", taskId],
      queryFn: () => this.apiClient.getTaskStats(taskId),
    })
  }

  getWarmupInfo(projectKey: ProjectKey, repository: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["getWarmupInfo", normalizeProjectKey(projectKey), repository],
      queryFn: () => this.apiClient.getWarmupInfo(projectKey, repository),
    })
  }

  listPreviewSetupTasks(projectKey: ProjectKey, repository: string, batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: ["previewSetupTasks", normalizeProjectKey(projectKey), repository, batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.listPreviewSetupTasks(projectKey, repository, batchInfo),

      staleTime: 60_000,
    })
  }

  listProfiles(batchInfo: BatchInfo, query: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["listProfiles", batchInfo.offset, batchInfo.batchSize, query],
      queryFn: () => this.apiClient.listProfiles(batchInfo, query),
    })
  }

  listProjectSecrets(projectKey: ProjectKey) {
    return this.queryClient.fetchQuery({
      queryKey: projectSecretsQueryKey(projectKey),
      queryFn: () => this.apiClient.listProjectSecrets(projectKey),

      staleTime: 1000 * 60, // 60 seconds
    })
  }

  listProjectWarmupSnapshots(projectKey: ProjectKey, repoName: string) {
    return this.queryClient.fetchQuery({
      queryKey: projectWarmupSnapshotsQueryKey(projectKey),
      queryFn: () => this.apiClient.listProjectWarmupSnapshots(projectKey, repoName),

      staleTime: 1000 * 60, // 60 seconds
    })
  }

  listProjectsV2(batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: projectsV2QueryKey.concat(batchInfo.offset, batchInfo.batchSize),
      queryFn: () => this.apiClient.listProjectsV2(batchInfo, 3),

      // Make sure to invalidate the project list when creating/deleting a project
      staleTime: Infinity,
    })
  }

  listSshKeys(profile: UProfileIdentifier) {
    return this.queryClient.fetchQuery({
      queryKey: ["listSshKeys", profile],
      queryFn: () => this.apiClient.listSshKeys(profile),
    })
  }

  listTasks(projectKey: ProjectKey, repo: string, principalId: string | null, successful: boolean, withCollaborators: boolean, batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: ["tasks", normalizeProjectKey(projectKey), repo, principalId, successful, withCollaborators, batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.listTasks(projectKey, repo, principalId, successful, withCollaborators, batchInfo),

      // Make sure to invalidate the task list when creating/deleting a task
      staleTime: 1000 * 60, // 60 seconds
    })
  }

  listTasksForUser(userId: TID, successful: boolean, batchInfo: BatchInfo) {
    return this.queryClient.fetchQuery({
      queryKey: ["tasks", userId, successful, batchInfo.offset, batchInfo.batchSize],
      queryFn: () => this.apiClient.listTasksForUser(userId, successful, batchInfo),

      // Make sure to invalidate the task list when creating/deleting a task
      staleTime: 1000 * 60 * 5, // 5 minutes
    })
  }

  private trackedLiveUpdateDebugEvents = new Map<"key", Observable<AppDateTime>>()

  liveUpdateDebugEvents() {
    return getOrCreateSharedObservable(this.trackedLiveUpdateDebugEvents, "key", () => this.apiClient.liveUpdatesDebugEvents())
  }

  llmLog(taskId: string, pageId: string | null, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return this.queryClient.fetchQuery({
      queryKey: ["llmLog", taskId, pageId, batchInfo.offset, batchInfo.batchSize, sorting],
      queryFn: () => this.apiClient.llmLog(taskId, pageId, batchInfo, sorting),
    })
  }

  llmLogPages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return this.queryClient.fetchQuery({
      queryKey: ["llmLogPages", taskId, batchInfo.offset, batchInfo.batchSize, sorting],
      queryFn: () => this.apiClient.llmLogPages(taskId, batchInfo, sorting),
    })
  }

  llmLogRequestContext(logEntryId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["llmLogRequestContext", logEntryId],
      queryFn: () => this.apiClient.llmLogRequestContext(logEntryId),
    })
  }

  private trackedLlmLogPageUpdates = new Map<string, Observable<LLMLogPage>>()

  llmLogPageUpdates(taskId: string, pageId: string | null) {
    const key = `${taskId}:${pageId ?? "null"}`
    return getOrCreateSharedObservable(this.trackedLlmLogPageUpdates, key, () => this.apiClient.llmLogPageUpdates(taskId, pageId))
  }

  private trackedLlmLogUpdates = new Map<string, Observable<LLMLogEntry>>()

  llmLogUpdates(taskId: string, pageId: string | null) {
    const key = `${taskId}:${pageId ?? "null"}`
    return getOrCreateSharedObservable(this.trackedLlmLogUpdates, key, () => this.apiClient.llmLogUpdates(taskId, pageId))
  }

  me() {
    return this.queryClient.fetchQuery({
      queryKey: myPrincipalQueryKey,
      queryFn: () => this.apiClient.me(),

      // This entity is managed externally, so this query might become stale. For now, we accept this tradeoff.
      staleTime: Infinity,
    })
  }

  message(messageId: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["message", messageId],
      queryFn: () => this.apiClient.message(messageId),
    })
  }

  private trackedMessageUpdates = new Map<string, Observable<AiChatEvent>>()

  messageUpdates(taskId: string) {
    return getOrCreateSharedObservable(this.trackedMessageUpdates, taskId, () => this.apiClient.messageUpdates(taskId))
  }

  messages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return this.queryClient.fetchQuery({
      queryKey: ["messages", batchInfo.offset, batchInfo.batchSize, sorting, taskId],
      queryFn: () => this.apiClient.messages(taskId, batchInfo, sorting),
    })
  }

  profile(profile: ProfileIdentifier.Username) {
    return this.queryClient.fetchQuery({
      queryKey: ["profile", profile.username],
      queryFn: () => this.apiClient.profile(profile),

      // This entity is managed externally, so this query might become stale. For now, we accept this tradeoff.
      staleTime: Infinity,
    })
  }

  resolvePrincipals(pids: string[]) {
    return this.queryClient.fetchQuery({
      queryKey: ["resolvePrincipals", ...pids],
      queryFn: () => this.apiClient.resolvePrincipals(pids),
    })
  }

  subscribeToFigmaAuthStatus() {
    return this.queryClient.fetchQuery({
      queryKey: ["subscribeToFigmaAuthStatus"],
      queryFn: () => this.apiClient.subscribeToFigmaAuthStatus(),
    })
  }

  subscribeToOpenedPullRequests(taskIds: string[]) {
    return this.queryClient.fetchQuery({
      queryKey: ["subscribeToOpenedPullRequests", ...taskIds],
      queryFn: () => this.apiClient.subscribeToOpenedPullRequests(taskIds),
    })
  }

  private trackedSubscribeToPreviewProcess = new Map<string, Observable<UPreviewProcessesApiState>>()

  subscribeToPreviewProcess(previewId: string | null, previewV2Id: string | null) {
    return getOrCreateSharedObservable(this.trackedSubscribeToPreviewProcess, `${previewId}/${previewV2Id}`, () => this.apiClient.subscribeToPreviewProcessState(previewId as TID | null, previewV2Id as TID | null))
  }

  subscribeToRepositorySyncErrors(projectKey: ProjectKey, repository: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["subscribeToRepositorySyncErrors", normalizeProjectKey(projectKey), repository],
      queryFn: () => this.apiClient.subscribeToRepositorySyncErrors(projectKey, repository),
    })
  }

  private trackedSubscribeToTypingEvents = new Map<string, Observable<TypingEventDTO>>()

  subscribeToTypingEvents(taskId: TID) {
    return getOrCreateSharedObservable(this.trackedSubscribeToTypingEvents, taskId, () => this.apiClient.subscribeToTypingEvents(taskId))
  }

  private trackedSubscribeToPreviewAppLogs = new Map<string, Observable<string>>()

  subscribeToPreviewAppLogs(id: string | null, previewEnvId: string | null, logId: string) {
    return getOrCreateSharedObservable(this.trackedSubscribeToPreviewAppLogs, `${id}/${previewEnvId}/${logId}`, () => this.apiClient.subscribeToPreviewAppLogs(id as TID | null, previewEnvId as TID | null, logId))
  }

  private trackedSubscribeToPreviewLogList = new Map<string, Observable<AvailableLog[]>>()

  subscribeToPreviewLogList(id: string | null, previewV2Id: string | null) {
    return getOrCreateSharedObservable(this.trackedSubscribeToPreviewLogList, `${id}/${previewV2Id}`, () => this.apiClient.subscribeToPreviewLogList(id as TID | null, previewV2Id as TID | null))
  }

  private trackedSubscribeToPreviewReadyEstimate = new Map<TID, Observable<number>>()

  subscribeToPreviewReadyEstimate(id: TID) {
    return getOrCreateSharedObservable(this.trackedSubscribeToPreviewReadyEstimate, id, () => this.apiClient.subscribeToPreviewReadyEstimate(id))
  }

  task(taskId: string) {
    return this.queryClient.fetchQuery({
      ...taskKey(taskId),
      queryFn: () => this.apiClient.task(taskId)
    })
  }

  taskDetails(taskId: string) {
    return this.queryClient.fetchQuery({
      ...taskDetailsKey(taskId),
      queryFn: () => this.apiClient.taskDetails(taskId),

      // Fetched when entering a task page, make sure to invalidate it when leaving
      staleTime: Infinity,
    })
  }

  private trackedTaskUpdates = new Map<string, Observable<AiAgentTaskEvent>>()

  taskUpdates(taskId: string) {
    return getOrCreateSharedObservable(this.trackedTaskUpdates, taskId, () => this.apiClient.taskUpdates(taskId))
  }

  validateRemoteUrl(url: string) {
    return this.queryClient.fetchQuery({
      queryKey: ["validateRemoteUrl", url],
      queryFn: () => this.apiClient.validateRemoteUrl(url),

      staleTime: Infinity,
    })
  }
}

export const projectsV2QueryKey: unknown[] = ["projectsV2"]
export const favoriteProjectsQueryKey = ["favoriteProjects"]

export function projectQueryKey(projectKey: ProjectKey): { queryKey: unknown[] } {
  return {
    queryKey: projectsV2QueryKey.concat(normalizeProjectKey(projectKey)),
  }
}

function getOrCreateSharedObservable<T>(
  map: Map<string, Observable<T>>,
  key: string,
  create: () => Promise<Observable<T>>,
): Observable<T> {
  const existing = map.get(key)
  if (existing) {
    console.log(`getOrCreateSharedObservable: returning existing observable for key ${key}`)
    return existing
  }

  console.log(`getOrCreateSharedObservable: creating new observable for key ${key}`)
  const shared = from(create()).pipe(
    switchMap(inner => inner),
    shareReplay({ bufferSize: 1, refCount: true }),
    finalize(() => {
      map.delete(key)
    })
  )

  map.set(key, shared)
  return shared
}

export function projectSecretsQueryKey(projectKey: ProjectKey) {
  return ["projectSecrets", normalizeProjectKey(projectKey)]
}

export function projectWarmupSnapshotsQueryKey(projectKey: ProjectKey) {
  return ["listProjectWarmupSnapshots", normalizeProjectKey(projectKey)]
}

export function projectRuntimeConfigQueryKey(projectKey: ProjectKey, repository: string) {
  return ["getProjectRuntimeConfig", normalizeProjectKey(projectKey), repository]
}

export function projectCodingGuidelinesQueryKey(projectKey: ProjectKey, repository: string) {
  return ["getProjectCodingGuidelines", normalizeProjectKey(projectKey), repository]
}
